import React, { useEffect, useMemo, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { renderString } from 'nunjucks';
import useAsync from 'react-use/esm/useAsync';
import { WorkspacesService } from '../../services/WorkspacesService';
import { Environment } from '../../pages/workspaces/types';
import { SupplyWorkspacePickerProps } from './schema';

export { SupplyWorkspacePickerSchema } from './schema';

const VALID_ENVIRONMENTS: readonly string[] = [
  'development',
  'staging',
  'production',
];
const SEARCH_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;
// Distance (px) from the bottom of the listbox that triggers loading the next page.
const LOAD_MORE_SCROLL_THRESHOLD = 32;
// Inputs that look like the beginning of a UUID are matched against the
// workspace racId; anything else is matched against the workspace name.
const RAC_ID_INPUT_PATTERN = /^[0-9a-fA-F]{4,8}(-[0-9a-fA-F-]*)?$/;

/**
 * Scaffolder field that picks workspaces of a system straight from the
 * OpenControl API (server-side search with infinite scroll), returning the
 * workspace tenants (racIds) as the field value.
 */
export const SupplyWorkspacePicker = (props: SupplyWorkspacePickerProps) => {
  const {
    onChange,
    schema: {
      title = 'Workspaces',
      description = 'Workspaces provisioned for the system',
    },
    required,
    uiSchema,
    rawErrors,
    formData,
    idSchema,
    formContext,
  } = props;

  const catalogApi = useApi(catalogApiRef);
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const authApi = useApi(microsoftAuthApiRef);

  const workspacesService = useMemo(
    () => new WorkspacesService(fetchApi, authApi, discoveryApi, catalogApi),
    [fetchApi, authApi, discoveryApi, catalogApi],
  );

  const context = { parameters: formContext.formData };
  const systemRef = renderString(
    uiSchema['ui:options']?.system ?? '',
    context,
  ).trim();
  const environment = renderString(
    uiSchema['ui:options']?.environment ?? '',
    context,
  )
    .trim()
    .toLowerCase();

  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [options, setOptions] = useState<string[]>([]);
  const [labels, setLabels] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | undefined>();

  const { value: systemId } = useAsync(async () => {
    if (!systemRef) {
      return undefined;
    }
    const entity = await catalogApi.getEntityByRef(`system:${systemRef}`);
    return WorkspacesService.getSystemId(entity ?? undefined);
  }, [catalogApi, systemRef]);

  // Debounce typed input into the effective search term, restarting paging.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchTerm(inputValue.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [inputValue]);

  // Fetch the current page; page 1 replaces the options, later pages append.
  useEffect(() => {
    if (!systemId || !VALID_ENVIRONMENTS.includes(environment)) {
      setOptions([]);
      setHasNext(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setSearchError(undefined);
      try {
        const filter = searchTerm
          ? RAC_ID_INPUT_PATTERN.test(searchTerm)
            ? { partialRacId: searchTerm }
            : { partialName: searchTerm }
          : {};
        const response = await workspacesService.getWorkspacesBySystem(
          environment as Environment,
          systemId,
          { ...filter, page, pageSize: SEARCH_PAGE_SIZE },
        );
        if (cancelled) {
          return;
        }
        setLabels(previous => {
          const merged = new Map(previous);
          response.items.forEach(workspace => {
            merged.set(
              workspace.racId,
              WorkspacesService.toWorkspaceOption(workspace).name,
            );
          });
          return merged;
        });
        const pageIds = response.items.map(workspace => workspace.racId);
        setOptions(previous =>
          page === 1
            ? pageIds
            : [...previous, ...pageIds.filter(id => !previous.includes(id))],
        );
        setHasNext(response.hasNext);
      } catch (error) {
        if (!cancelled) {
          setOptions([]);
          setHasNext(false);
          setSearchError(
            error instanceof Error
              ? error.message
              : 'Failed to fetch workspaces.',
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [workspacesService, systemId, environment, searchTerm, page]);

  const handleListboxScroll = (event: React.SyntheticEvent) => {
    const listbox = event.currentTarget as HTMLElement;
    const nearBottom =
      listbox.scrollTop + listbox.clientHeight >=
      listbox.scrollHeight - LOAD_MORE_SCROLL_THRESHOLD;
    if (nearBottom && hasNext && !loading) {
      setPage(previous => previous + 1);
    }
  };

  const helperText =
    searchError ??
    (systemRef && systemId === undefined && !loading
      ? `System '${systemRef}' has no supplyadmin appcode annotation`
      : hasNext
      ? `${description} — scroll the list to load more, or type to refine`
      : description);

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData?.length}
    >
      <Autocomplete
        multiple
        filterSelectedOptions
        id={idSchema?.$id}
        value={formData ?? []}
        loading={loading}
        options={options}
        // Server-side search: options already reflect the typed filter.
        filterOptions={candidates => candidates}
        getOptionLabel={racId => labels.get(racId) ?? racId}
        onChange={(_event, value) => onChange(value)}
        onInputChange={(_event, value) => setInputValue(value)}
        ListboxProps={{ onScroll: handleListboxScroll }}
        renderInput={params => (
          <TextField
            {...params}
            label={title}
            margin="dense"
            helperText={helperText}
            error={Boolean(searchError)}
            FormHelperTextProps={{
              margin: 'dense',
              style: { marginLeft: 0 },
            }}
            variant="outlined"
            required={required}
            InputProps={{
              ...params.InputProps,
              required: formData?.length === 0 && required,
            }}
          />
        )}
      />
    </FormControl>
  );
};
